import { useState, useEffect, useRef } from "react";
import { RiCloseLargeFill, RiDeleteBinLine, RiAddFill } from "react-icons/ri";
import DynamicSelect from "../../DynamicSelect";
import { toast } from "react-toastify";
import axios from "axios";
import CreatableSelect from "react-select/creatable";
import { PrintBillLayout } from "../../PrintBillLayout";

const API_BASE_URL = "https://api.muktihospital.com/api";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const EmergencyBillModal = ({ isOpen, onClose, billId, initialData, onSave }) => {
  const [formData, setFormData] = useState({
    patientName: "",
    billingDate: new Date().toISOString().split("T")[0],
    refer: "",
    referAmount: "",
    billNumber: "",
    authorizedBy: "",
    consultantName: "",
  });

  const [authorizedByOptions, setAuthorizedByOptions] = useState([]);
  const [selectedBillId, setSelectedBillId] = useState(billId || null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  
  const [serviceCharge, setServiceCharge] = useState("0");
  const [discount, setDiscount] = useState("0");
  const [paidAmount, setPaidAmount] = useState("0");

  const [services, setServices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [consultants, setConsultants] = useState([]); 
  
  const [billResponse, setBillResponse] = useState({
    billAmount: 0,
    serviceChargePc: 0,
    serviceCharge: 0,
    totalAmount: 0,
    discountPc: 0,
    discount: 0,
    netAmount: 0,
    paidAmount: 0,
    payableAmount: 0,
  });

  const [billNumberSearch, setBillNumberSearch] = useState("");
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatientOption, setSelectedPatientOption] = useState(null);
  const [billNumberOptions, setBillNumberOptions] = useState([]);
  const [allBills, setAllBills] = useState([]);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);
  const searchRef = useRef(null);

  const calculateBillAmounts = (billData) => {
    let billAmount = 0;
    if (billData.serviceItems && Array.isArray(billData.serviceItems)) {
      billAmount = billData.serviceItems.reduce(
        (sum, item) => sum + (parseFloat(item.unitPrice || 0) * parseFloat(item.totalUnit || 0)),
        0
      );
    }

    const serviceChargeVal = parseFloat(billData.serviceCharge || 0);
    const totalAmount = billAmount + serviceChargeVal;

    const discountVal = parseFloat(billData.discount || 0);
    const netAmount = totalAmount - discountVal;

    const paidVal = parseFloat(billData.paidAmount || 0);
    const payableAmount = netAmount - paidVal;

    return {
      billAmount: Math.round(billAmount),
      serviceCharge: Math.round(serviceChargeVal),
      totalAmount: Math.round(totalAmount),
      discount: Math.round(discountVal),
      netAmount: Math.round(netAmount),
      paidAmount: Math.round(paidVal),
      payableAmount: Math.round(payableAmount),
    };
  };

  useEffect(() => {
    const calculatedAmounts = calculateBillAmounts({
      serviceItems: tableData,
      serviceCharge,
      discount,
      paidAmount,
    });
    setBillResponse((prev) => ({
      ...prev,
      ...calculatedAmounts,
    }));
  }, [tableData, serviceCharge, discount, paidAmount]);

  useEffect(() => {
    if (!isOpen) return;
    
    const fetchData = async () => {
      try {
        const [servicesResp, agentsResp, billsResp, authoritiesResp, patientsResp, doctorsResp] = await Promise.all([
          axios.get(`${API_BASE_URL}/emergency/services`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/emergency/agents`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/emergency/emergency-bills`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/authorities`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/patient`, { headers: { "x-api-key": API_KEY } }),
          axios.get(`${API_BASE_URL}/doctor`, { headers: { "x-api-key": API_KEY } })
        ]);

        const servicesData = servicesResp.data?.services || [];
        setServices(
          servicesData.map((service) => ({
            value: service.id,
            label: service.serviceName || "Unknown",
            particulars: service.particulars || "",
            providedBy: service.providedBy || "",
            unitPrice: service.unitPrice || 0,
          }))
        );

        const agentsData = agentsResp.data?.agents || [];
        setAgents(
          agentsData.map((agent) => ({
            value: agent.id,
            label: agent.agentName || "Unknown",
          }))
        );

        const billsData = billsResp.data?.bills || billsResp.data || [];
        setAllBills(billsData);
        setBillNumberOptions(billsData.map((bill) => bill.billNumber || ""));

        const authorityList = authoritiesResp.data || [];
        setAuthorizedByOptions(
          authorityList.map((auth) => ({
            value: auth.name,
            label: auth.name,
          }))
        );

        const patientsData = patientsResp.data || [];
        setPatientOptions(patientsData.map(p => ({
            value: p.id,
            label: `${p.name} (${p.phoneNumber || "No Phone"})`,
            nameOnly: p.name,
            phone: p.phoneNumber || ""
        })));

        const doctorsData = Array.isArray(doctorsResp.data) ? doctorsResp.data : (doctorsResp.data.data || []);
        setConsultants(
          doctorsData.map((d) => {
             let name = d.name || "Unknown";
             if(d.translations) {
                 try {
                     const t = typeof d.translations === 'string' ? JSON.parse(d.translations) : d.translations;
                     name = t?.en?.name || name;
                 } catch(e){}
             }
             return { value: name, label: name }; 
          })
        );

      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast.error("Failed to fetch initial data.", { autoClose: 2000 });
      }
    };
    fetchData();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (billId || initialData) {
        const loadBill = async () => {
          setIsLoading(true);
          try {
            const response = initialData
              ? { data: initialData }
              : await axios.get(`${API_BASE_URL}/emergency/emergency-bills/${billId}`, {
                  headers: { "x-api-key": API_KEY },
                });
            const bill = response.data.bill || response.data;
            setSelectedBillId(bill.id || billId);
            
            const agent = agents.find((a) => a.value === bill.refer);
            setFormData({
              patientName: bill.patientName || "",
              billingDate: bill.billingDate ? bill.billingDate.split("T")[0] : new Date().toISOString().split("T")[0],
              refer: agent ? agent.label : bill.refer || "",
              referAmount: bill.referAmount || "",
              billNumber: bill.billNumber || "",
              authorizedBy: bill.authorizedBy || "",
              consultantName: bill.consultantName || "",
            });

            if (bill.patientName) {
              const foundPatient = patientOptions.find(p => p.nameOnly === bill.patientName || p.label.includes(bill.patientName));
              if (foundPatient) {
                  setSelectedPatientOption(foundPatient);
              } else {
                  const tempOption = {
                      label: bill.patientName,
                      value: bill.patientId || "existing_patient",
                      nameOnly: bill.patientName,
                      phone: bill.patient?.phoneNumber || ""
                  };
                  setSelectedPatientOption(tempOption);
              }
            }
            
            setTableData(
              bill.serviceItems?.map((item) => ({
                serviceId: item.serviceName || "",
                particulars: item.particulars || "",
                providedBy: item.providedBy || "",
                unitPrice: item.unitPrice || 0,
                totalUnit: item.totalUnit || "",
                amount: item.amount || 0,
              })) || [{ serviceId: "", particulars: "", providedBy: "", unitPrice: 0, totalUnit: "", amount: 0 }]
            );
            
            setServiceCharge(bill.serviceCharge || "0");
            setDiscount(bill.discount || "0");
            setPaidAmount(bill.paidAmount || "0");
            setBillResponse(bill);
          } catch (error) {
            console.error("Error loading bill:", error);
            toast.error("Failed to load bill data", { autoClose: 2000 });
          } finally {
            setIsLoading(false);
          }
        };
        loadBill();
      } else {
        handleReset();
        setSelectedBillId(null);
      }
    }
  }, [isOpen, billId, initialData, agents]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "billNumber") {
      setBillNumberSearch(value);
      if (value) {
        const filteredOptions = allBills
          .filter((bill) =>
            bill.billNumber.toLowerCase().includes(value.toLowerCase())
          )
          .map((bill) => bill.billNumber);
        setBillNumberOptions(filteredOptions);
      } else {
        setBillNumberOptions(allBills.map((bill) => bill.billNumber));
      }
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (field) => (selectedOption) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        [field]: selectedOption.label, 
      }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handlePatientSelectChange = (selectedOption) => {
    setSelectedPatientOption(selectedOption);
    if (selectedOption) {
      setFormData(prev => ({
        ...prev,
        patientName: selectedOption.nameOnly || selectedOption.label,
      }));
    } else {
      setFormData(prev => ({ ...prev, patientName: "" }));
    }
  };

  const handleCreatePatient = (inputValue) => {
    const newOption = { value: `new_${Date.now()}`, label: inputValue, nameOnly: inputValue, phone: "" };
    setSelectedPatientOption(newOption);
    setPatientOptions(prev => [...prev, newOption]);
    setFormData(prev => ({ ...prev, patientName: inputValue }));
    toast.info(`New patient "${inputValue}" added.`);
  };

  const handleTableInputChange = (index, e) => {
    const { name, value } = e.target;
    const newTableData = [...tableData];
    newTableData[index] = { ...newTableData[index], [name]: value };

    if (name === "totalUnit" || name === "unitPrice") {
      const unitPrice = parseFloat(name === "unitPrice" ? value : newTableData[index].unitPrice) || 0;
      const totalUnit = parseFloat(name === "totalUnit" ? value : newTableData[index].totalUnit) || 0;
      
      newTableData[index].amount = Math.round(totalUnit * unitPrice);

      if (name === "totalUnit" && value && !isNaN(totalUnit) && totalUnit > 0) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          if (newTableData.every((row) => row.totalUnit && parseFloat(row.totalUnit) > 0)) {
            delete newErrors.totalUnit;
          }
          return newErrors;
        });
      }
    }
    setTableData(newTableData);
  };

  const handleServiceChange = (index) => (selectedOption) => {
    const serviceId = selectedOption ? selectedOption.value : "";
    const selectedService = services.find((service) => service.value === serviceId);
    const newTableData = [...tableData];
    newTableData[index] = {
      ...newTableData[index],
      serviceId: selectedService ? selectedService.label : "",
      particulars: selectedService?.particulars || "",
      providedBy: selectedService?.providedBy || "",
      unitPrice: selectedService?.unitPrice || 0,
      amount: Math.round(parseFloat(newTableData[index].totalUnit || 0) * (selectedService?.unitPrice || 0)),
    };
    setTableData(newTableData);
  };

  const handleAddRow = () => {
    setTableData([...tableData, { serviceId: "", particulars: "", providedBy: "", unitPrice: 0, totalUnit: "", amount: 0 }]);
  };

  const handleRemoveRow = (index) => {
    if (tableData.length > 1) {
      setTableData(tableData.filter((_, i) => i !== index));
    }
  };

  const handleOtherInputChange = (setter) => (e) => {
    setter(e.target.value);
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleReset = () => {
    setFormData({
      patientName: "",
      // mobileNumber removed
      billingDate: new Date().toISOString().split("T")[0],
      refer: "",
      referAmount: "",
      billNumber: "",
      authorizedBy: "",
      consultantName: "",
    });
    setTableData([{ serviceId: "", particulars: "", providedBy: "", unitPrice: 0, totalUnit: "", amount: 0 }]);
    setServiceCharge("0");
    setDiscount("0");
    setPaidAmount("0");
    setErrors({});
    setSelectedBillId(null);
    setBillResponse({
      billAmount: 0,
      serviceCharge: 0,
      totalAmount: 0,
      discount: 0,
      netAmount: 0,
      paidAmount: 0,
      payableAmount: 0,
    });
    setBillNumberSearch("");
    setBillNumberOptions([]);
    setAllBills([]);
  };

  const handleSelectBillNumber = async (billNumber) => {
    if (!billNumber) return;

    setIsLoading(true);
    try {
      const bill = allBills.find((b) => b.billNumber === billNumber);
      if (!bill) {
        throw new Error("Bill not found");
      }

      const response = await axios.get(`${API_BASE_URL}/emergency/emergency-bills/${bill.id}`, {
        headers: { "x-api-key": API_KEY },
      });
      const fetchedBill = response.data.bill || response.data;
      setSelectedBillId(fetchedBill.id);

      setFormData({
        patientName: fetchedBill.patientName || "",
        // mobileNumber removed
        billingDate: fetchedBill.billingDate ? fetchedBill.billingDate.split("T")[0] : new Date().toISOString().split("T")[0],
        refer: fetchedBill.refer || "",
        referAmount: fetchedBill.referAmount || "",
        billNumber: fetchedBill.billNumber || "",
        authorizedBy: fetchedBill.authorizedBy || "",
        consultantName: fetchedBill.consultantName || "",
      });
      setTableData(fetchedBill.serviceItems || []);
      setServiceCharge(fetchedBill.serviceCharge || "0");
      setDiscount(fetchedBill.discount || "0");
      setPaidAmount(fetchedBill.paidAmount || "0");
      setBillResponse(fetchedBill);

      setBillNumberSearch("");
      setBillNumberOptions([]);
      setErrors((prev) => ({ ...prev, billNumber: "" }));
    } catch (error) {
      console.error("Error loading bill by number:", error);
      setErrors((prev) => ({ ...prev, billNumber: "No bill found with this number" }));
      toast.error("No bill found with this number", { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

const handleSaveAndPrint = async (e) => {
  e.preventDefault();
  const newErrors = {};
  if (!formData.patientName) newErrors.patientName = "Patient name is required";
  if (!formData.billingDate) newErrors.billingDate = "Billing Date is required";
  if (!formData.authorizedBy) newErrors.authorizedBy = "Authorizer is required";
  if (tableData.some((row) => !row.serviceId)) newErrors.serviceId = "Service selection is required for all rows";
  if (tableData.some((row) => !row.totalUnit || parseFloat(row.totalUnit) <= 0)) {
    newErrors.totalUnit = "Total Unit must be a positive number for all rows";
  }
  if (!paidAmount) newErrors.paidAmount = "Paid amount is required";

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    toast.error("Please fill all required fields", { autoClose: 2000 });
    return;
  }

  setIsLoading(true);
  try {
    const payload = {
      patientName: formData.patientName,
      // mobileNumber removed
      billingDate: formData.billingDate,
      refer: formData.refer,
      referAmount: parseFloat(formData.referAmount) || 0,
      authorizedBy: formData.authorizedBy,
      consultantName: formData.consultantName, 
      serviceCharge: parseFloat(serviceCharge) || 0,
      discount: parseFloat(discount) || 0,
      paidAmount: parseFloat(paidAmount) || 0,
      serviceItems: tableData.map((row) => ({
        serviceName: row.serviceId,
        particulars: row.particulars,
        providedBy: row.providedBy,
        unitPrice: parseFloat(row.unitPrice) || 0,
        totalUnit: parseInt(row.totalUnit) || 0,
        amount: parseFloat(row.amount) || 0,
      })),
    };

    let response;
    if (selectedBillId) {
      response = await axios.put(
        `${API_BASE_URL}/emergency/emergency-bills/edit/${selectedBillId}`,
        payload,
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success("Bill updated successfully!", { autoClose: 2000 });
    } else {
      response = await axios.post(
        `${API_BASE_URL}/emergency/emergency-bills/generate`,
        payload,
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success("Bill saved successfully!", { autoClose: 2000 });
    }

    const savedBill = response.data.bill || response.data;
    setBillResponse(savedBill);
    setSelectedBillId(savedBill.id);
    setFormData((prev) => ({ ...prev, billNumber: savedBill.billNumber || "" }));

    // Footer for Print
    const signatureFooter = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0px', paddingRight: '10px' }}>
          <div style={{ textAlign: 'center', width: '220px' }}>
            <div style={{ borderTop: '1px solid #333', height: '1px', marginBottom: '8px' }}></div>
            <p style={{ margin: '0', fontWeight: 'bold', fontSize: '12pt' }}>
              {formData.authorizedBy || "N/A"} 
            </p>
            <p style={{ margin: '5px 0 0 0', fontSize: '10pt' }}>
              Authorized By
            </p>
          </div>
        </div>
      );

    const billInfo = (
      <>
        <div>
          <p><strong>Bill No:</strong> {savedBill.billNumber || "N/A"}</p>
          <p><strong>Patient Name:</strong> {formData.patientName || "N/A"}</p>
          <p><strong>Billing Date:</strong> {formData.billingDate ? new Date(formData.billingDate).toLocaleDateString("en-US") : "N/A"}</p>
        </div>
        <div>
          <p><strong>Refer:</strong> {formData.refer || "N/A"}</p>
          <p><strong>Refer Amount:</strong> ৳{parseFloat(formData.referAmount || 0).toFixed(2)}</p>
          <p><strong>Consultant:</strong> {formData.consultantName || "N/A"}</p>
          <p><strong>Authorized By:</strong> {formData.authorizedBy || "N/A"}</p>
        </div>
      </>
    );

    const table = (
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-2 sm:p-3 border font-semibold">Service Name</th>
            <th className="p-2 sm:p-3 border font-semibold">Particulars</th>
            <th className="p-2 sm:p-3 border font-semibold">Provided By</th>
            <th className="p-2 sm:p-3 border font-semibold text-right">Unit Price (৳)</th>
            <th className="p-2 sm:p-3 border font-semibold text-right">Total Unit</th>
            <th className="p-2 sm:p-3 border font-semibold text-right">Amount (৳)</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr key={index} className="border-t">
              <td className="p-2 sm:p-3 border">{row.serviceId || "N/A"}</td>
              <td className="p-2 sm:p-3 border">{row.particulars || "N/A"}</td>
              <td className="p-2 sm:p-3 border">{row.providedBy || "N/A"}</td>
              <td className="p-2 sm:p-3 border text-right">{parseFloat(row.unitPrice || 0).toFixed(2)}</td>
              <td className="p-2 sm:p-3 border text-right">{row.totalUnit || "0"}</td>
              <td className="p-2 sm:p-3 border text-right">{parseFloat(row.amount || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

    const summary = (
      <div className="flex flex-col">
        <div className="flex justify-between"><span>Bill Amount:</span><span>৳{Math.round(billResponse?.billAmount || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Service Charge:</span><span>৳{Math.round(billResponse?.serviceCharge || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Total Amount:</span><span>৳{Math.round(billResponse?.totalAmount || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Discount:</span><span>৳{Math.round(billResponse?.discount || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Net Amount:</span><span>৳{Math.round(billResponse?.netAmount || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Paid Amount:</span><span>৳{Math.round(billResponse?.paidAmount || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Payable Amount:</span><span>৳{Math.round(billResponse?.payableAmount || 0).toFixed(2)}</span></div>
      </div>
    );

    const newPrintData = { billInfo, table, summary, signatureFooter };
    setPrintData(newPrintData);
    setShowPrintPreview(true);

    setTimeout(() => {
      const elementsToHide = document.querySelectorAll(".print-hidden, .print-hidden-in-print");
      elementsToHide.forEach((el) => (el.style.display = "none"));
      const printLayout = document.querySelector(".print-preview");
      if (printLayout) printLayout.style.display = "block";
      window.print();
      setTimeout(() => {
        elementsToHide.forEach((el) => (el.style.display = ""));
        if (printLayout) printLayout.style.display = "none";
        setShowPrintPreview(false);
        onSave(savedBill, true);
      }, 500);
    }, 1000);

  } catch (error) {
    console.error("Error saving/updating bill:", error);
    toast.error("Failed to save/update bill", { autoClose: 2000 });
  } finally {
    setIsLoading(false);
  }
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.patientName) { toast.error("Patient Name is required"); return; }
    // Mobile number validation removed
    if (!formData.billingDate) newErrors.billingDate = "Billing Date is required";
    if (!formData.authorizedBy) newErrors.authorizedBy = "Authorizer is required";
    if (tableData.some((row) => !row.serviceId)) newErrors.serviceId = "Service selection is required for all rows";
    if (tableData.some((row) => !row.totalUnit || parseFloat(row.totalUnit) <= 0)) {
      newErrors.totalUnit = "Total Unit must be a positive number for all rows";
    }
    if (!paidAmount) newErrors.paidAmount = "Paid amount is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields", { autoClose: 2000 });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        patientName: formData.patientName,
        billingDate: formData.billingDate,
        refer: formData.refer,
        referAmount: parseFloat(formData.referAmount) || 0,
        authorizedBy: formData.authorizedBy,
        consultantName: formData.consultantName,
        serviceCharge: parseFloat(serviceCharge) || 0,
        discount: parseFloat(discount) || 0,
        paidAmount: parseFloat(paidAmount) || 0,
        serviceItems: tableData.map((row) => ({
          serviceName: row.serviceId,
          particulars: row.particulars,
          providedBy: row.providedBy,
          unitPrice: parseFloat(row.unitPrice) || 0,
          totalUnit: parseInt(row.totalUnit) || 0,
          amount: parseFloat(row.amount) || 0,
        })),
      };

      let response;
      if (selectedBillId) {
        response = await axios.put(
          `${API_BASE_URL}/emergency/emergency-bills/edit/${selectedBillId}`,
          payload,
          { headers: { "x-api-key": API_KEY } }
        );
        toast.success("Bill updated successfully!", { autoClose: 2000 });
      } else {
        response = await axios.post(
          `${API_BASE_URL}/emergency/emergency-bills/generate`,
          payload,
          { headers: { "x-api-key": API_KEY } }
        );
        toast.success("Bill saved successfully!", { autoClose: 2000 });
      }
      
      const savedBill = response.data.bill || response.data;
      setBillResponse(savedBill);
      setSelectedBillId(savedBill.id);
      setFormData((prev) => ({ ...prev, billNumber: savedBill.billNumber || "" }));
      onSave(savedBill, false);
    } catch (error) {
      console.error("Error saving/updating bill:", error);
      toast.error("Failed to save/update bill", { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white shadow-lg w-full max-w-full max-h-[100vh] h-full overflow-y-auto print-wrapper">
        <div className="bg-[#ececec] p-5 flex flex-col md:flex-row justify-between items-center mb-4 border-b border-dashed border-gray-300">
          <div className="flex flex-col items-center md:items-start">
            <h2 className="text-xl font-semibold text-gray-700">
              {selectedBillId ? "Edit Emergency Bill" : "Generate Emergency Bill"}
            </h2>
          </div>
          <div className="flex items-start justify-center flex-wrap gap-2 mt-3 xl:mt-0 relative z-50">
            <div className="relative flex items-center gap-2">
              <input
                type="text"
                name="billNumber"
                value={formData.billNumber}
                onChange={handleInputChange}
                ref={searchRef}
                className={`w-64 p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.billNumber ? "border-red-500" : "border-gray-300"
                } bg-gray-50 shadow-sm`}
                placeholder="Search Bill Number"
                autoComplete="off"
              />
              {billNumberSearch && billNumberOptions.length > 0 && (
                <ul className="absolute top-full left-0 w-64 bg-white border border-gray-300 mt-1 rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
                  {billNumberOptions.map((billNumber, index) => (
                    <li
                      key={index}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSelectBillNumber(billNumber)}
                    >
                      {billNumber}
                    </li>
                  ))}
                </ul>
              )}   
              {errors.billNumber && (
                <p className="text-red-500 text-xs mt-1.5">
                  {errors.billNumber}
                </p>
              )}
            </div>
            <button
              title="Close"
              className="size-7 rounded-full bg-white text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
              onClick={onClose}
              disabled={isLoading}
            >
              <RiCloseLargeFill />
            </button>
          </div>
        </div>
        <div className="my-4 px-6 h-[calc(100vh-150px)] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {/* Patient Name */}
            <div>
                <label className="label">
                  Patient's Name: <span className="text-red-600">*</span>
                </label>
                <CreatableSelect
                  isClearable
                  isSearchable
                  options={patientOptions}
                  value={selectedPatientOption}
                  onChange={handlePatientSelectChange}
                  onCreateOption={handleCreatePatient}
                  placeholder="Select or Type New Patient Name"
                  formatCreateLabel={(inputValue) => `Add new patient: "${inputValue}"`}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: '#e2e8f0',
                        fontSize: '14px',
                        backgroundColor: '#f7fafc',
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        fontSize: '14px',
                        backgroundColor: state.isSelected
                          ? '#3b82f6'
                          : state.isFocused
                          ? '#ebf8ff'
                          : 'transparent',
                        color: state.isSelected ? '#ffffff' : '#2d3748',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        padding: '0px',
                      }),
                    }}
                />
                {/* Hidden input to ensure HTML5 'required' validation works */}
                <input 
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ opacity: 0, height: 0, position: 'absolute' }}
                  value={formData.patient}
                  required
                  onChange={()=>{}}
                />
              </div>
              
              <div>
                <label className="label font-medium text-black">
                  Billing Date <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  name="billingDate"
                  value={formData.billingDate}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.billingDate ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.billingDate && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.billingDate}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">Refer</label>
                <DynamicSelect
                  options={agents}
                  value={agents.find((option) => option.label === formData.refer) || null}
                  onChange={handleSelectChange("refer")}
                  isSearchable
                  placeholder="Select Refer"
                  className={`w-full rounded-md border ${
                    errors.refer ? "border-red-500" : "border-gray-300"
                  } bg-gray-50 shadow-sm`}
                />
                {errors.refer && (
                  <p className="text-red-500 text-xs mt-1.5">{errors.refer}</p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Refer Amount
                </label>
                <input
                  type="number"
                  name="referAmount"
                  value={formData.referAmount}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.referAmount ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter refer amount"
                  min="0"
                />
                {errors.referAmount && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.referAmount}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Authorized By <span className="text-red-600">*</span>
                </label>
                <DynamicSelect
                  options={authorizedByOptions}
                  value={
                    formData.authorizedBy
                      ? authorizedByOptions.find(
                          (option) => option.label === formData.authorizedBy
                        )
                      : null
                  }
                  onChange={handleSelectChange("authorizedBy")}
                  isSearchable
                  placeholder="Select Authorizer"
                  className={`w-full rounded-md border ${
                    errors.authorizedBy ? "border-red-500" : "border-gray-300"
                  } bg-gray-50 shadow-sm`}
                />
                {errors.authorizedBy && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.authorizedBy}
                  </p>
                )}
              </div>
              
              {/* Consultant Dropdown */}
              <div>
                <label className="label font-medium text-black">
                  Consultant Name
                </label>
                <DynamicSelect
                  options={consultants}
                  value={
                    formData.consultantName
                      ? consultants.find((c) => c.label === formData.consultantName) || 
                        { label: formData.consultantName, value: formData.consultantName }
                      : null
                  }
                  onChange={handleSelectChange("consultantName")}
                  isSearchable
                  placeholder="Select Consultant"
                  className="w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm"
                />
              </div>

            </div>
            <div className="overflow-x-auto col-span-2 mt-6">
              <h2 className="text-lg font-bold text-gray-700 mb-3">Services</h2>
              <table className="w-full table-fixed border-collapse min-w-[1000px] mb-5">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Service Name <span className="text-red-600">*</span>
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Particulars
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Provided By
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Unit Price
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Total Unit <span className="text-red-600">*</span>
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Amount
                    </th>
                    <th className="text-center text-xs text-black border border-gray-300 py-2 px-3 w-16 font-medium">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index}>
                      <td className="border border-gray-200 py-2 px-3">
                        <DynamicSelect
                          options={services}
                          value={
                            services.find(
                              (service) => service.label === row.serviceId
                            ) || null
                          }
                          onChange={handleServiceChange(index)}
                          placeholder="Service"
                          isSearchable
                          className={`w-full rounded-md border ${
                            errors.serviceId ? "border-red-500" : "border-gray-300"
                          } bg-gray-50 shadow-sm`}
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="text"
                          name="particulars"
                          value={row.particulars}
                          onChange={(e) => handleTableInputChange(index, e)}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Particulars"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="text"
                          name="providedBy"
                          value={row.providedBy}
                          onChange={(e) => handleTableInputChange(index, e)}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Provided By"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          name="unitPrice"
                          value={row.unitPrice}
                          onChange={(e) => handleTableInputChange(index, e)}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          name="totalUnit"
                          value={row.totalUnit}
                          onChange={(e) => handleTableInputChange(index, e)}
                          className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.totalUnit ? "border-red-500" : "border-gray-300"
                          }`}
                          placeholder="0"
                          min="0"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          name="amount"
                          value={Math.round(row.amount)}
                          readOnly
                          className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                          placeholder="0"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <button
                          title="Remove"
                          className="size-7 mx-auto rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          onClick={() => handleRemoveRow(index)}
                          disabled={tableData.length === 1}
                        >
                          <RiDeleteBinLine />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Bill Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.billAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <button
                        title="Add Row"
                        className="size-7 mx-auto rounded-full bg-[#73CB64] text-white text-sm flex items-center justify-center hover:bg-M-primary-color transition-all duration-300"
                        onClick={handleAddRow}
                      >
                        <RiAddFill />
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Service Charge (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        name="serviceCharge"
                        value={serviceCharge}
                        onChange={handleOtherInputChange(setServiceCharge)}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.serviceCharge ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      {errors.serviceCharge && (
                        <p className="text-red-500 text-xs mt-1.5">
                          {errors.serviceCharge}
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Total Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.totalAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Discount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        name="discount"
                        value={discount}
                        onChange={handleOtherInputChange(setDiscount)}
                        readOnly={!!selectedBillId}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.discount ? "border-red-500" : "border-gray-300"
                          }
                          ${selectedBillId ? "bg-gray-100 cursor-not-allowed" : ""}
                          `}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      {errors.discount && (
                        <p className="text-red-500 text-xs mt-1.5">
                          {errors.discount}
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Net Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.netAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Paid (৳) <span className="text-red-600">*</span>
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        name="paidAmount"
                        value={paidAmount}
                        onChange={handleOtherInputChange(setPaidAmount)}
                        readOnly={!!selectedBillId}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.discount ? "border-red-500" : "border-gray-300"
                          }
                          ${selectedBillId ? "bg-gray-100 cursor-not-allowed" : ""}
                          `}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      {errors.paidAmount && (
                        <p className="text-red-500 text-xs mt-1.5">
                          {errors.paidAmount}
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={5}
                    >
                      Payable (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.payableAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </form>
        </div>
        <div className="flex justify-end items-center gap-3 px-6 fixed w-full bottom-0 p-3 bg-gray-200 z-20 border-t border-dashed border-M-text-color/50">
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="bg-gray-600 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-gray-800 transition-all duration-300"
              disabled={isLoading}
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="bg-[#E7633D]/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndPrint}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {selectedBillId ? "Update & Print" : "Save & Print"}
            </button>
            <button
              onClick={handleSubmit}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {selectedBillId ? "Update" : "Save"}
            </button>
          </div>
        </div>
        {showPrintPreview && printData && (
          <div className="print-preview">
            <PrintBillLayout
              className="print-preview"
              title="Emergency Bill"
              billInfo={printData.billInfo}
              table={printData.table}
              summary={printData.summary}
              footer={printData.signatureFooter}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmergencyBillModal;